import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { useState, useMemo } from 'react';
import { Info } from 'lucide-react';
import type { CustomerSubscription } from '@/domain/entities/subscription.entity';

type DelayOption = '1_week' | '2_weeks' | '3_weeks' | 'custom';

function addDaysToDate(dateStr: string | null | undefined, days: number): Date {
  const base = dateStr ? new Date(dateStr) : new Date();
  const result = new Date(base);
  result.setUTCDate(result.getUTCDate() + days);
  return result;
}

function formatDateShort(date: Date): string {
  const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(date.getUTCDate()).padStart(2, '0');
  const yy = String(date.getUTCFullYear()).slice(-2);
  return `${mm}/${dd}/${yy}`;
}

function toISODateString(date: Date): string {
  const yyyy = date.getUTCFullYear();
  const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(date.getUTCDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function getTomorrowISO(): string {
  const tomorrow = new Date();
  tomorrow.setUTCDate(tomorrow.getUTCDate() + 1);
  return toISODateString(tomorrow);
}

function getMaxDateISO(): string {
  const max = new Date();
  max.setUTCMonth(max.getUTCMonth() + 6);
  return toISODateString(max);
}

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
`;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(41, 41, 41, 0.4);
  z-index: 1000;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  animation: ${fadeIn} 0.2s ease-out;

  @media (min-width: 768px) {
    align-items: center;
  }
`;

const Sheet = styled.div`
  width: 100%;
  max-width: 480px;
  background: linear-gradient(
    0deg,
    #fff 94%,
    rgba(255, 237, 234, 1) 98%,
    rgba(245, 186, 176, 1) 100%
  );
  border-radius: 16px 16px 0 0;
  display: flex;
  flex-direction: column;
  max-height: 90vh;
  overflow-y: auto;
  animation: ${slideUp} 0.3s ease-out;

  @media (min-width: 768px) {
    border-radius: 16px;
  }
`;

const SheetContent = styled.div`
  padding: 32px 24px 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Title = styled.h2`
  font-size: 24px;
  font-weight: 700;
  line-height: 1.17;
  color: #292929;
  margin: 0;
  text-align: center;
`;

const Subtitle = styled.p`
  font-size: 12px;
  font-weight: 600;
  line-height: 1.37;
  color: #292929;
  margin: 0;
  text-align: center;
`;

const OptionsGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const OptionRow = styled.label<{ isSelected?: boolean }>`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 0 14px;
  height: 37px;
  background: #f7f7f7;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.15s;

  ${({ isSelected }) => isSelected && `background: #e8e8e8;`}
`;

const HiddenRadio = styled.input`
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
`;

const RadioCircle = styled.span<{ checked: boolean }>`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid ${({ checked }) => (checked ? '#f7a08b' : '#808080')};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: border-color 0.15s;

  &::after {
    content: '';
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${({ checked }) => (checked ? '#f7a08b' : 'transparent')};
    transition: background 0.15s;
  }
`;

const OptionLabel = styled.span`
  font-size: 14px;
  font-weight: 500;
  line-height: 2.29;
  color: #292929;
`;

const DateInputWrapper = styled.div`
  padding: 0 14px;
`;

const StyledDateInput = styled.input`
  width: 100%;
  padding: 10px 14px;
  border: 1px solid #d1d1d1;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  color: #292929;
  background: #fff;
  outline: none;
  font-family: inherit;

  &:focus {
    border-color: #f7a08b;
  }
`;

const InfoBar = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 14px;
  height: 36px;
  background: #d1d1d1;
  border-radius: 6px;
`;

const InfoIcon = styled.span`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  color: #292929;
`;

const InfoText = styled.span`
  font-size: 12px;
  font-weight: 500;
  line-height: 1.17;
  color: #292929;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #e5e5e5;
  margin: 0;
`;

const CTASection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
`;

const SaveButton = styled.button`
  width: 100%;
  padding: 10px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  background: #292929;
  border: none;
  color: #fff;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  transition: opacity 0.15s;

  &:hover:not(:disabled) {
    opacity: 0.85;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CancelTextButton = styled.button`
  background: none;
  border: none;
  padding: 12px;
  font-size: 16px;
  font-weight: 400;
  color: #292929;
  cursor: pointer;
  text-decoration: underline;
  transition: opacity 0.15s;
  align-self: center;

  &:hover:not(:disabled) {
    opacity: 0.7;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

interface RescheduleSheetProps {
  readonly subscription: CustomerSubscription;
  readonly isLoading: boolean;
  readonly onConfirm: (newDate: string) => void;
  readonly onCancel: () => void;
}

const DELAY_OPTIONS: { value: DelayOption; label: string; days: number }[] = [
  { value: '1_week', label: 'Delay for 1 week', days: 7 },
  { value: '2_weeks', label: 'Delay for 2 weeks', days: 14 },
  { value: '3_weeks', label: 'Delay for 3 weeks', days: 21 },
  { value: 'custom', label: 'Choose a specific date', days: 0 },
];

export function RescheduleSheet({
  subscription,
  isLoading,
  onConfirm,
  onCancel,
}: RescheduleSheetProps) {
  const [selected, setSelected] = useState<DelayOption | null>(null);
  const [customDate, setCustomDate] = useState('');

  const computedDate = useMemo(() => {
    if (!selected) return null;
    if (selected === 'custom') {
      return customDate ? new Date(customDate + 'T00:00:00') : null;
    }
    const option = DELAY_OPTIONS.find((o) => o.value === selected);
    if (!option) return null;
    return addDaysToDate(subscription.next_charge_date, option.days);
  }, [selected, customDate, subscription.next_charge_date]);

  const canSave = computedDate !== null;

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && !isLoading) {
      onCancel();
    }
  };

  const handleSave = () => {
    if (!computedDate) return;
    onConfirm(toISODateString(computedDate));
  };

  return (
    <Backdrop onClick={handleBackdropClick} data-testid="reschedule-backdrop">
      <Sheet
        role="dialog"
        aria-modal="true"
        aria-label="Reschedule subscription"
      >
        <SheetContent>
          <Title>When do you want to get your product?</Title>

          <Subtitle>Schedule changes will apply to all future orders</Subtitle>

          <OptionsGroup role="radiogroup" aria-label="Delay options">
            {DELAY_OPTIONS.map((option) => (
              <OptionRow
                key={option.value}
                isSelected={selected === option.value}
                htmlFor={`reschedule-${option.value}`}
              >
                <HiddenRadio
                  type="radio"
                  id={`reschedule-${option.value}`}
                  name="reschedule-option"
                  value={option.value}
                  checked={selected === option.value}
                  onChange={() => setSelected(option.value)}
                  data-testid={`reschedule-option-${option.value}`}
                />
                <RadioCircle checked={selected === option.value} />
                <OptionLabel>{option.label}</OptionLabel>
              </OptionRow>
            ))}
          </OptionsGroup>

          {selected === 'custom' && (
            <DateInputWrapper>
              <StyledDateInput
                type="date"
                value={customDate}
                min={getTomorrowISO()}
                max={getMaxDateISO()}
                onChange={(e) => setCustomDate(e.target.value)}
                aria-label="Choose a date"
                data-testid="reschedule-date-input"
              />
            </DateInputWrapper>
          )}

          {computedDate && (
            <InfoBar data-testid="reschedule-info-bar">
              <InfoIcon>
                <Info size={14} />
              </InfoIcon>
              <InfoText>
                Your subscription will be rescheduled for{' '}
                {formatDateShort(computedDate)}
              </InfoText>
            </InfoBar>
          )}

          <Divider />

          <CTASection>
            <SaveButton
              type="button"
              onClick={handleSave}
              disabled={!canSave || isLoading}
              data-testid="reschedule-save-button"
            >
              {isLoading ? 'Saving...' : 'Save changes'}
            </SaveButton>
            <CancelTextButton
              type="button"
              onClick={onCancel}
              disabled={isLoading}
              data-testid="reschedule-cancel-button"
            >
              Cancel
            </CancelTextButton>
          </CTASection>
        </SheetContent>
      </Sheet>
    </Backdrop>
  );
}
